import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { toast } from "react-toastify";
import InspectionForm from "./InspectionForm";
import InspectionDetail from "./InspectionDetail";
import { inspectionRequest, errorMessage } from "./inspectionApi";
import { RATINGS, STATUSES, fullName, formatDate } from "./inspectionUi";
import "./MachineInspections.css";

export default function MachineInspections({ user = {} }) {
  const [params, setParams] = useSearchParams();
  const detailId = params.get("inspection");
  const machineId = params.get("machine") || "";
  const role = user?.role?.uid || user?.role;
  const canCreate = ["admin", "superAdmin", "technician", "support"].includes(role);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    rating: "",
    page: 1,
  });
  const [result, setResult] = useState({
    items: [],
    total: 0,
    pages: 0,
    summary: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const query = new URLSearchParams({
          ...filters,
          limit: 12,
          ...(machineId ? { machineId } : {}),
        });
        const data = await inspectionRequest(`?${query}`);
        if (active) {
          setResult(data);
          setError("");
        }
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [filters, machineId, refresh]);
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetailLoading(true);
    setDetail(null);
    setDetailError("");
    inspectionRequest(`/${detailId}`)
      .then((data) => {
        if (active) setDetail(data);
      })
      .catch((err) => {
        if (active) setDetailError(errorMessage(err));
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [detailId, refresh]);
  const openDetail = (id) => {
    const next = new URLSearchParams(params);
    next.set("inspection", id);
    setParams(next);
  };
  const closeDetail = () => {
    const next = new URLSearchParams(params);
    next.delete("inspection");
    setParams(next);
  };
  const refreshData = useCallback(() => setRefresh((prev) => prev + 1), []);
  const onUpdated = (item) => {
    setDetail(item);
    toast.success("Seguimiento guardado");
    refreshData();
  };
  const clearFilters = () => {
    setFilters({ search: "", status: "", rating: "", page: 1 });
    setParams({});
  };
  const summary = result.summary;
  return (
    <main className="mi-page">
      <header className="mi-header">
        <div>
          <div className="mi-eyebrow">
            <span /> SERVICIO TÉCNICO
          </div>
          <h1>Inspecciones de máquinas</h1>
          <p>
            {role === "operator"
              ? "Tus avisos, evidencias y correcciones, en un solo lugar."
              : "Observa el estado de cada máquina y acompaña su mejora."}
          </p>
        </div>
        {canCreate && (
          <Button
            label="Nueva inspección"
            icon="pi pi-plus"
            onClick={() => setCreating(true)}
          />
        )}
      </header>
      <div className="mi-stats" aria-label="Resumen de inspecciones">
        <div>
          <span className="mi-stat-icon blue">
            <i className="pi pi-list" />
          </span>
          <div>
            <span>Total de inspecciones</span>
            <strong>{summary.total ?? "—"}</strong>
          </div>
        </div>
        <div>
          <span className="mi-stat-icon amber">
            <i className="pi pi-clock" />
          </span>
          <div>
            <span>Por atender</span>
            <strong>{summary.pending ?? "—"}</strong>
          </div>
        </div>
        <div>
          <span className="mi-stat-icon red">
            <i className="pi pi-exclamation-triangle" />
          </span>
          <div>
            <span>Prioritarias abiertas</span>
            <strong>{summary.priority ?? "—"}</strong>
          </div>
        </div>
        <div>
          <span className="mi-stat-icon teal">
            <i className="pi pi-check-square" />
          </span>
          <div>
            <span>Por verificar</span>
            <strong>{summary.verification ?? "—"}</strong>
          </div>
        </div>
      </div>
      {summary.unassigned > 0 && (
        <button
          type="button"
          className="mi-unassigned"
          onClick={() => setFilter("status", "unassigned")}
        >
          <span>
            <i className="pi pi-user-plus" /> {summary.unassigned}{" "}
            {summary.unassigned === 1
              ? "inspección necesita"
              : "inspecciones necesitan"}{" "}
            un responsable
          </span>
          <span>
            Ver pendientes <i className="pi pi-arrow-right" />
          </span>
        </button>
      )}
      <section className="mi-list-section">
        <div className="mi-list-heading">
          <div>
            <h2>
              {machineId
                ? "Historial de la máquina"
                : ["operator", "support"].includes(role)
                ? "Mis inspecciones"
                : "Todas las inspecciones"}
            </h2>
            <span>Las más recientes aparecen primero</span>
          </div>
          <Button
            icon="pi pi-refresh"
            aria-label="Actualizar inspecciones"
            className="p-button-text p-button-rounded"
            onClick={refreshData}
            disabled={loading}
          />
        </div>
        <div className="mi-filters">
          <span className="p-input-icon-left mi-search">
            <i className="pi pi-search" />
            <InputText
              aria-label="Buscar inspecciones"
              value={filters.search}
              maxLength={100}
              onChange={(e) => setFilter("search", e.target.value)}
              placeholder="Buscar máquina, ruta o ubicación"
            />
          </span>
          <Dropdown
            aria-label="Filtrar por estado"
            value={filters.status}
            options={[
              { label: "Todos los estados", value: "" },
              ...Object.entries(STATUSES).map(([value, label]) => ({
                value,
                label,
              })),
              ...(role !== "operator"
                ? [{ value: "unassigned", label: "Sin responsable" }]
                : []),
            ]}
            onChange={(e) => setFilter("status", e.value)}
          />
          <Dropdown
            aria-label="Filtrar por calificación"
            value={filters.rating}
            options={[
              { label: "Todas las calificaciones", value: "" },
              ...Object.entries(RATINGS)
                .filter(([key]) => key !== "na")
                .map(([value, item]) => ({ value, label: item.label })),
            ]}
            onChange={(e) => setFilter("rating", e.value)}
          />
          {(filters.search ||
            filters.status ||
            filters.rating ||
            machineId) && (
            <Button
              label="Limpiar"
              className="p-button-text"
              onClick={clearFilters}
            />
          )}
        </div>
        <div aria-live="polite" aria-busy={loading}>
          {loading ? (
            <div className="mi-card-grid">
              {[1, 2, 3, 4, 5, 6].map((key) => (
                <div className="mi-inspection-card" key={key}>
                  <Skeleton width="45%" height="1.5rem" />
                  <Skeleton width="80%" height="2rem" className="mt-4" />
                  <Skeleton height="5rem" className="mt-3" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="mi-empty">
              <i className="pi pi-exclamation-circle" />
              <h3>No pudimos cargar las inspecciones</h3>
              <p role="alert">{error}</p>
              <Button label="Reintentar" onClick={refreshData} />
            </div>
          ) : result.items.length ? (
            <div className="mi-card-grid">
              {result.items.map((item) => (
                <button
                  type="button"
                  key={item._id}
                  className={`mi-inspection-card mi-card-button ${item.rating}`}
                  onClick={() => openDetail(item._id)}
                >
                  <div className="mi-card-top">
                    <span className={`mi-badge ${item.rating}`}>
                      <i className={RATINGS[item.rating].icon} />
                      {RATINGS[item.rating].label}
                    </span>
                    <small>
                      {item.machineType === "coffee" ? "CAFÉ" : "SNACK"}
                    </small>
                  </div>
                  <h3>{item.snapshot.name}</h3>
                  <p className="mi-card-location">
                    <i className="pi pi-map-marker" />
                    {item.snapshot.location || "Sin ubicación"}
                  </p>
                  <div className="mi-card-meta">
                    <span>
                      <i className="pi pi-directions" />
                      {item.snapshot.route || "Sin ruta"}
                    </span>
                    <span>
                      <i className="pi pi-user" />
                      {fullName(item.assignedTo)}
                    </span>
                  </div>
                  <div className="mi-card-bottom">
                    <span className={`mi-status ${item.status}`}>
                      {STATUSES[item.status]}
                    </span>
                    <i className="pi pi-arrow-up-right" />
                  </div>
                  <small className="mi-card-date">
                    {formatDate(item.createdAt)}
                  </small>
                </button>
              ))}
            </div>
          ) : (
            <div className="mi-empty">
              <span className="mi-empty-icon">
                <i className="pi pi-check-square" />
              </span>
              <h3>
                {filters.search || filters.status || filters.rating || machineId
                  ? "No hay coincidencias"
                  : "Todo empieza con una mirada"}
              </h3>
              <p>
                {filters.search || filters.status || filters.rating || machineId
                  ? "Prueba otros filtros para encontrar una inspección."
                  : canCreate
                  ? "Registra la primera inspección con fotos y una evaluación de la máquina."
                  : "Aquí aparecerán las inspecciones que te asignen."}
              </p>
              {canCreate && !machineId && (
                <Button
                  label="Crear inspección"
                  icon="pi pi-plus"
                  onClick={() => setCreating(true)}
                />
              )}
            </div>
          )}
        </div>
        {!error && result.total > 0 && (
          <footer className="mi-pagination">
            <span>
              {result.total}{" "}
              {result.total === 1 ? "inspección" : "inspecciones"} · Página{" "}
              {filters.page} de {Math.max(1, result.pages)}
            </span>
            <div>
              <Button
                icon="pi pi-chevron-left"
                aria-label="Página anterior"
                className="p-button-text"
                disabled={loading || filters.page <= 1}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, page: prev.page - 1 }))
                }
              />
              <Button
                icon="pi pi-chevron-right"
                aria-label="Página siguiente"
                className="p-button-text"
                disabled={loading || filters.page >= result.pages}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                }
              />
            </div>
          </footer>
        )}
      </section>
      <Dialog
        header="Nueva inspección"
        visible={creating}
        onHide={() => {}}
        closable={false}
        closeOnEscape={false}
        modal
        className="mi-dialog"
        style={{ width: "min(850px, 96vw)" }}
      >
        <InspectionForm
          onCancel={() => setCreating(false)}
          onCreated={(item) => {
            setCreating(false);
            refreshData();
            openDetail(item._id);
            toast.success("Inspección registrada");
          }}
        />
      </Dialog>
      <Dialog
        header="Detalle de inspección"
        visible={Boolean(detailId)}
        onHide={closeDetail}
        modal
        className="mi-dialog"
        style={{ width: "min(960px, 96vw)" }}
      >
        {detailLoading ? (
          <div className="mi-loading">
            <Skeleton height="10rem" />
            <Skeleton height="15rem" className="mt-3" />
          </div>
        ) : detailError ? (
          <div className="mi-empty">
            <p role="alert">{detailError}</p>
            <Button label="Reintentar" onClick={refreshData} />
          </div>
        ) : (
          detail && (
            <InspectionDetail
              item={detail}
              onUpdated={onUpdated}
              onDeleted={() => {
                closeDetail();
                setDetail(null);
                setFilters((prev) => ({ ...prev, page: 1 }));
                refreshData();
                toast.success("Inspección eliminada");
              }}
              onRefresh={refreshData}
              onHistory={(id) => {
                setFilters({ search: "", status: "", rating: "", page: 1 });
                setParams({ machine: id });
              }}
            />
          )
        )}
      </Dialog>
    </main>
  );
}
